import { router } from '@inertiajs/react'
import PriorityDropdown from './PriorityDropdown'
import TopicContextMenu from './TopicContextMenu'
import { TOPIC_CATEGORIES } from '../../types'
import type { Topic } from '../../types'

interface Props {
  topic: Topic
  teamId: number
  onEdit: (topic: Topic) => void
  onResolve: (topic: Topic) => void
  onConvert: (topic: Topic) => void
  onPriority?: (topic: Topic, value: boolean) => void
  onEdge?: (topic: Topic, edge: 'top' | 'bottom') => void
}

// Card presentation of a team topic for the grid view. Mirrors the key info
// and actions on TopicRow (owner, category, priority, context menu).
export default function TopicCard({ topic, teamId, onEdit, onResolve, onConvert, onPriority, onEdge }: Props) {
  const handlePriorityChange = (priority: boolean) => {
    if (onPriority) return onPriority(topic, priority)
    router.put(`/teams/${teamId}/topics/${topic.id}`, {
      topic: { priority }
    }, { preserveScroll: true, preserveState: true })
  }

  const categoryLabel = topic.category && (topic.category as string) !== 'none'
    ? (TOPIC_CATEGORIES.find(c => c.value === topic.category)?.label
       ?? topic.category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()))
    : null

  return (
    <div
      className="flex flex-col gap-3 p-4 bg-white border border-stone rounded-lg hover:shadow-sm transition-shadow cursor-pointer h-full"
      onClick={() => onEdit(topic)}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-base font-semibold text-text-primary leading-snug">{topic.title}</p>
        <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0 -mt-1 -mr-1">
          <TopicContextMenu
            topic={topic}
            teamId={teamId}
            onPriority={() => handlePriorityChange(!topic.priority)}
            onEdge={onEdge ? edge => onEdge(topic, edge) : undefined}
            onEdit={() => onEdit(topic)}
            onResolve={() => onResolve(topic)}
            onConvert={() => onConvert(topic)}
          />
        </div>
      </div>

      {topic.description?.trim() && (
        <p className="text-sm text-text-secondary line-clamp-2">{topic.description}</p>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        {topic.priority && <span className="text-xs font-bold text-text-primary">PRIORITY</span>}
        {categoryLabel && (
          <span className="text-xs text-text-muted">{categoryLabel}</span>
        )}
        {topic.topic_type === 'long_term' && (
          <span className="text-xs font-bold text-text-primary bg-[#351E1D]/10 px-1.5 py-0.5 rounded">Camp</span>
        )}
        {topic.created_at && (
          <span className="text-xs text-text-muted">
            Created {new Date(topic.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </span>
        )}
      </div>

      <div className="flex items-center justify-between gap-2 mt-auto pt-2 border-t border-stone/40">
        <div className="flex items-center gap-2 min-w-0">
          {topic.owner && (
            <>
              <div
                className="w-7 h-7 rounded-full bg-charcoal text-white text-xs font-bold flex items-center justify-center flex-shrink-0"
                title={`${topic.owner.first_name} ${topic.owner.last_name}`}
              >
                {topic.owner.first_name?.charAt(0)}{topic.owner.last_name?.charAt(0)}
              </div>
              <span className="text-sm font-medium text-text-secondary truncate">
                {topic.owner.first_name} {topic.owner.last_name}
              </span>
            </>
          )}
        </div>
        <div onClick={(e) => e.stopPropagation()} className="flex-shrink-0">
          <PriorityDropdown value={topic.priority} onChange={handlePriorityChange} />
        </div>
      </div>
    </div>
  )
}
